import { FloatingPortal } from "@floating-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { type LucideIcon } from "lucide-react";
import { ZIndex } from "@/constants";

export type ContextMenuItemType = {
  id: string;
  label: string;
  onClick: () => void;
  ReactIcon?: LucideIcon;
  isDivider?: boolean;
};

export type ContextMenuPosition = {
  x: number;
  y: number;
};

interface ContextMenuProps {
  position: ContextMenuPosition | null;
  items: ContextMenuItemType[];
  onClose: () => void;
}

export function ContextMenu({ position, items, onClose }: ContextMenuProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjustedPosition, setAdjustedPosition] =
    useState<ContextMenuPosition | null>(null);

  // Handle click outside to close
  useEffect(() => {
    if (!position) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    // Small delay to avoid closing immediately from the contextmenu event
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [position, onClose]);

  // Adjust position to keep menu in viewport
  useEffect(() => {
    if (!position) {
      setAdjustedPosition(null);
      return;
    }

    // Start with the original position
    let x = position.x;
    let y = position.y;

    // Wait for next frame to get menu dimensions
    requestAnimationFrame(() => {
      if (menuRef.current) {
        const rect = menuRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Adjust X if menu goes off right edge
        if (x + rect.width > viewportWidth - 16) {
          x = viewportWidth - rect.width - 16;
        }

        // Adjust Y if menu goes off bottom edge
        if (y + rect.height > viewportHeight - 16) {
          y = viewportHeight - rect.height - 16;
        }

        // Ensure menu doesn't go off left or top edge
        x = Math.max(16, x);
        y = Math.max(16, y);

        setAdjustedPosition({ x, y });
      }
    });

    // Set initial position immediately
    setAdjustedPosition(position);
  }, [position]);

  const handleItemClick = useCallback(
    (e: React.MouseEvent, item: ContextMenuItemType) => {
      e.preventDefault();
      e.stopPropagation();
      item.onClick();
      onClose();
    },
    [onClose],
  );

  if (!position || !adjustedPosition) return null;

  return (
    <FloatingPortal>
      <AnimatePresence>
        {position && (
          <motion.div
            ref={menuRef}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.1 }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            style={{
              position: "fixed",
              top: adjustedPosition.y,
              left: adjustedPosition.x,
              zIndex: ZIndex.APP.CONTEXT_MENU,
              outline: "none",
            }}
          >
            <div className="flex flex-col min-w-45 bg-popover [border:1px_solid_var(--border)] rounded-md shadow-md overflow-hidden p-1">
              {items.map((item, index) => {
                if (item.isDivider) {
                  return (
                    <div key={item.id} className="h-px bg-border mt-1 mb-1" />
                  );
                }

                const Icon = item.ReactIcon;

                return (
                  <button
                    key={item.id}
                    tabIndex={-1}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => handleItemClick(e, item)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px 12px",
                      backgroundColor:
                        activeIndex === index ? "var(--muted)" : "transparent",
                      border: "none",
                      borderRadius: "calc(var(--radius) - 4px)",
                      cursor: "pointer",
                      width: "100%",
                      textAlign: "left",
                      outline: "none",
                      fontSize: "0.75rem",
                      color: "var(--foreground)",
                      fontFamily: "var(--font-sans)",
                      transition: "background-color 0.1s ease",
                    }}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseLeave={() => setActiveIndex(null)}
                  >
                    {Icon && (
                      <Icon
                        size={14}
                        style={{
                          color: "var(--muted-foreground)",
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </FloatingPortal>
  );
}
